import { useCallback, useEffect, useRef, useState } from 'react';
import { describeMidi } from '@/lib/music/notes';
import { playMidi } from '@/lib/audio/synth';

interface PianoKeyboardProps {
  startMidi?: number;
  endMidi?: number;
  onKeyDown?: (midi: number) => void;
  /** chamado quando a tecla é solta (soltar o dedo ou o Note Off do MIDI) */
  onKeyUp?: (midi: number) => void;
  pressedMidi?: number | null;
  highlightMidis?: number[];
  /** rótulos especiais por MIDI, ex: { 60: 'Dó central' } */
  specialLabels?: Record<number, string>;
  showNoteNames?: boolean;
  feedback?: { midi: number; type: 'correct' | 'wrong' } | null;
  silent?: boolean;
  disabled?: boolean;
  className?: string;
}

const WHITE_W = 46;
const WHITE_H = 168;
const BLACK_W = 28;
const BLACK_H = 104;
const WHITE_SEMITONES = new Set([0, 2, 4, 5, 7, 9, 11]);

function isWhiteMidi(midi: number): boolean {
  return WHITE_SEMITONES.has(((midi % 12) + 12) % 12);
}

/**
 * Teclado de piano na tela, com teclas brancas e pretas na posição correta.
 * Tocar uma tecla emite som e envia o número MIDI para o exercício.
 */
export default function PianoKeyboard({
  startMidi = 48,
  endMidi = 72,
  onKeyDown,
  onKeyUp,
  pressedMidi = null,
  highlightMidis = [],
  specialLabels = {},
  showNoteNames = false,
  feedback = null,
  silent = false,
  disabled = false,
  className = '',
}: PianoKeyboardProps) {
  const [active, setActive] = useState<number[]>([]);
  const heldRef = useRef<Set<number>>(new Set());
  const onKeyUpRef = useRef(onKeyUp);

  useEffect(() => {
    onKeyUpRef.current = onKeyUp;
  });

  /** Solta uma tecla (idempotente): só dispara uma vez por tecla pressionada. */
  const release = useCallback((midi: number) => {
    if (!heldRef.current.has(midi)) return;
    heldRef.current.delete(midi);
    onKeyUpRef.current?.(midi);
  }, []);

  const releaseAll = useCallback(() => {
    heldRef.current.forEach((midi) => onKeyUpRef.current?.(midi));
    heldRef.current.clear();
  }, []);

  // Rede de segurança: se o dedo soltar fora da tecla, a nota ainda termina.
  useEffect(() => {
    if (!onKeyUp) return undefined;
    const handler = () => releaseAll();
    window.addEventListener('pointerup', handler);
    window.addEventListener('pointercancel', handler);
    return () => {
      window.removeEventListener('pointerup', handler);
      window.removeEventListener('pointercancel', handler);
    };
  }, [onKeyUp, releaseAll]);

  const press = useCallback(
    (midi: number) => {
      if (disabled) return;
      if (!silent) playMidi(midi, 0.8);
      heldRef.current.add(midi);
      setActive((prev) => (prev.includes(midi) ? prev : [...prev, midi]));
      window.setTimeout(() => {
        setActive((prev) => prev.filter((m) => m !== midi));
      }, 180);
      onKeyDown?.(midi);
    },
    [disabled, onKeyDown, silent],
  );

  const whiteKeys: { midi: number; index: number }[] = [];
  for (let midi = startMidi; midi <= endMidi; midi += 1) {
    if (isWhiteMidi(midi)) {
      whiteKeys.push({ midi, index: whiteKeys.length });
    }
  }
  const blackKeys: { midi: number; left: number }[] = [];
  for (let midi = startMidi; midi <= endMidi; midi += 1) {
    if (!isWhiteMidi(midi)) {
      const below = whiteKeys.find((key) => key.midi === midi - 1);
      if (below) {
        blackKeys.push({ midi, left: (below.index + 1) * WHITE_W - BLACK_W / 2 });
      }
    }
  }

  const totalWidth = whiteKeys.length * WHITE_W;
  const pressed = new Set<number>(active);
  if (pressedMidi !== null) pressed.add(pressedMidi);
  const highlighted = new Set(highlightMidis);

  return (
    <div className={`w-full overflow-x-auto ${className}`}>
      <div className="relative mx-auto" style={{ width: totalWidth, height: WHITE_H }}>
        {whiteKeys.map(({ midi, index }) => {
          const info = describeMidi(midi);
          const isPressed = pressed.has(midi);
          const isHighlight = highlighted.has(midi);
          const fbType = feedback?.midi === midi ? feedback.type : null;
          const special = specialLabels[midi];
          let colorClass = 'bg-background-50 hover:bg-background-100';
          if (isHighlight) colorClass = 'bg-primary-100';
          if (isPressed) colorClass = 'bg-primary-300';
          if (fbType === 'correct') colorClass = 'bg-accent-300';
          if (fbType === 'wrong') colorClass = 'bg-secondary-300';
          return (
            <button
              key={midi}
              type="button"
              aria-label={`Tecla ${info.label}`}
              onPointerDown={() => press(midi)}
              onPointerUp={() => release(midi)}
              onPointerLeave={() => release(midi)}
              onPointerCancel={() => release(midi)}
              disabled={disabled}
              className={`absolute bottom-0 flex flex-col justify-end items-center rounded-b-md border border-foreground-300 pb-2 transition-colors duration-100 ${colorClass} ${
                disabled ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
              style={{ left: index * WHITE_W, width: WHITE_W, height: WHITE_H }}
            >
              {special ? (
                <span className="text-[10px] font-label font-bold text-primary-700 leading-tight text-center px-0.5">
                  {special}
                </span>
              ) : showNoteNames ? (
                <span className="text-[10px] font-label font-medium text-foreground-500">
                  {info.label}
                </span>
              ) : null}
            </button>
          );
        })}

        {blackKeys.map(({ midi, left }) => {
          const info = describeMidi(midi);
          const isPressed = pressed.has(midi);
          const isHighlight = highlighted.has(midi);
          const fbType = feedback?.midi === midi ? feedback.type : null;
          let colorClass = 'bg-foreground-950 hover:bg-foreground-800';
          if (isHighlight) colorClass = 'bg-primary-600';
          if (isPressed) colorClass = 'bg-primary-700';
          if (fbType === 'correct') colorClass = 'bg-accent-600';
          if (fbType === 'wrong') colorClass = 'bg-secondary-600';
          return (
            <button
              key={midi}
              type="button"
              aria-label={`Tecla ${info.label}`}
              onPointerDown={() => press(midi)}
              onPointerUp={() => release(midi)}
              onPointerLeave={() => release(midi)}
              onPointerCancel={() => release(midi)}
              disabled={disabled}
              className={`absolute top-0 rounded-b-md border border-foreground-950 transition-colors duration-100 ${colorClass} ${
                disabled ? 'cursor-not-allowed' : 'cursor-pointer'
              }`}
              style={{ left, width: BLACK_W, height: BLACK_H }}
            />
          );
        })}
      </div>
    </div>
  );
}